import { HelpItemType, PopdockScriptDescription, PopdockScriptTitle, TileType } from "../constants";
import { HelpArticleItemListType, HelpArticleItemType } from "../help-article/help-article-constants";
import { normalizeArticles } from "./normalize-helper";
import { getFieldTypeNames, getHomeTiles, getOverloads, getSyntax } from "./popdock-script";

function getSelectedArticleMarkdown(selectedArticle, linkBaseUrl) {
    const { Title: title, Description: description, Articles: articles, Items: items, RelatedArticles: related } = selectedArticle;
    return getArticleMarkdown(title, description, articles, items, related, linkBaseUrl);
}

function getArticleMarkdown(title, description, articles, items, related, linkBaseUrl) {
    let markdown = `# ${title}\n\n`;
    if (description) markdown += `${description}\n\n`;
    markdown += getArticleItemMarkdown(items);
    markdown += getTileMarkdown(normalizeArticles(articles), linkBaseUrl);
    if (related?.length > 0) {
        markdown += "## See also\n\n";
        markdown += getTileMarkdown(normalizeArticles(related));
    }
    return markdown;
}

function getArticleItemMarkdown(items) {
    if (!items || items.length == 0)
        return "";

    let markdown = "";
    let number = 0;

    for (let i = 0, len = items.length; i < len; i++) {
        switch (items[i].type) {
            case HelpArticleItemType.Title:
                markdown += `## ${items[i].value}\n\n`;
                break;
            case HelpArticleItemType.Subtitle:
                markdown += `### ${items[i].value}\n\n`;
                break;
            case HelpArticleItemType.Text:
                markdown += `${items[i].value}\n\n`;
                break;
            case HelpArticleItemType.ListItem:
                if (items[i].listType === HelpArticleItemListType.Number) {
                    if (items[i].startOfList) {
                        number = 1;
                    } else {
                        number++;
                    }
                    markdown += `${number}. ${items[i].value}\n\n`;
                } else {
                    markdown += `- ${items[i].value}\n\n`;
                }
                break;
            case HelpArticleItemType.Tip:
                markdown += `> ${items[i].value}\n\n`;
                break;
            case HelpArticleItemType.Code:
                markdown += `\`\`\`${items[i].codeType}\n${items[i].value}\n\`\`\`\n\n`;
                break;
        }
    }

    return markdown;
}

function getPopdockScriptFunctionMarkdown(func) {
    let markdown = `# ${func.Name}\n\n`;
    if (func.Description) markdown += `${func.Description}\n\n`;
    if (func.LongDescription) markdown += `${func.LongDescription}\n\n`;

    const requiredArgs = func.Arguments.filter(arg => !arg.Optional && !arg.UiOnly);
    const optionalArgs = func.Arguments.filter(arg => arg.Optional && !arg.UiOnly);
    const overloads = getOverloads(optionalArgs);

    const syntax = overloads.map(overload => {
        return getSyntax(func.Name, requiredArgs, overload)
    }).join("\n\n");

    if (syntax) markdown += `## Syntax\n\n${syntax}\n\n`;

    const funcArgs = func.Arguments.filter(arg => !arg.UiOnly).map(arg => {
        let argMarkdown = `### ${arg.Name}\n\n`;
        if (arg.Description) argMarkdown += `${arg.Description}\n\n`;

        const fieldTypes = getFieldTypeNames(arg.FieldTypes);
        if (fieldTypes?.length > 0) argMarkdown += `#### Field types:\n\n${fieldTypes.map(fieldType => `- ${fieldType}`).join("\n")}\n\n`;

        if (arg.ListItems?.length > 0) {
            const listItems = arg.ListItems.map(listItem => {
                const description = listItem.Description ? ` :${listItem.Description}` : "";
                return `- ${listItem.Value}${description}`;
            }).join("\n");

            if (listItems) argMarkdown += `#### Valid values:\n\n${listItems}`;
        }
        return argMarkdown
    }).join("\n\n");

    if (funcArgs) markdown += `## Arguments\n\n${funcArgs}`;

    const examples = func.Examples.map(example => {
        let exampleMarkdown = `### ${example.Name}\n\n`;
        if (example.Description) exampleMarkdown += `${example.Description}\n\n`;
        return exampleMarkdown + `#### Function:\n\n${example.Function}\n\n#### Result:\n\n${example.Result}`;
    }).join("\n\n");

    if (examples) markdown += `## Examples\n\n${examples}\n\n`;

    const relatedItems = func.Related.map(relatedItem => `- ${relatedItem.Name}`).join("\n");
    if (relatedItems) markdown += `## Related items\n\n${relatedItems}`;
    return markdown;
}

function getTilePageMarkdown(title, description, tiles, longDescription, linkBaseUrl) {
    let markdown = `# ${title}\n\n`;
    if (description) markdown += `${description}\n\n`;
    if (longDescription) markdown += `${longDescription}\n\n`;
    markdown += getTileMarkdown(tiles, linkBaseUrl);
    return markdown;
}

function getTileMarkdown(tiles, linkBaseUrl) {
    if (!tiles || tiles.length == 0)
        return "";

    let markdown = "";

    for (const tile of tiles) {
        if (!tile.Name) continue;
        if (tile.Type === TileType.Subtitle) {
            markdown += `## ${tile.Name}\n\n`;
        } else {
            const url = linkBaseUrl && (tile.Slug || tile.Id) ? ` (${linkBaseUrl}/${tile.Slug ?? tile.Id}.md)` : ""; //todo: should fix this and just have Slug messed up introducing Id
            markdown += `### ${tile.Name}${url}\n\n`;
            if (tile.Description) markdown += `${tile.Description}\n\n`;
        }
    }

    return markdown;
}

function getSelectedItemMarkdownFunction(selectedItem, linkBaseUrl) {
    switch (selectedItem.Type) {
        case HelpItemType.Article:
            return () => getSelectedArticleMarkdown(selectedItem, linkBaseUrl);
        case HelpItemType.Category:
            return () => getTilePageMarkdown(selectedItem.Name, selectedItem.Description, selectedItem.Functions, selectedItem.LongDescription, linkBaseUrl);
        case HelpItemType.Function:
            return () => getPopdockScriptFunctionMarkdown(selectedItem);
        default:
            return null;
    }
}

function getTopLevelPopdockScriptPageMarkdown(articles, functions, linkBaseUrl) {
    return getTilePageMarkdown(PopdockScriptTitle, PopdockScriptDescription, getHomeTiles(articles, functions), null, linkBaseUrl);
}

function buildMarkdownFooter(siteName, itemUrl) {
    return `\n\n---\n\n[View this page on ${siteName}](${itemUrl})\n`;
}

export {
    getSelectedArticleMarkdown,
    getArticleMarkdown,
    getPopdockScriptFunctionMarkdown,
    getTilePageMarkdown,
    getSelectedItemMarkdownFunction,
    getTopLevelPopdockScriptPageMarkdown,
    buildMarkdownFooter
}